"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import * as echarts from "echarts";
import { Card, CardContent, CardHeader, CardTitle, Progress } from "@repo/ui";
import { formatDateTime, formatDuration } from "@repo/utils";
import {
  User,
  Clock,
  Calendar,
  ChevronDown,
  ChevronUp,
  Star,
  AlertTriangle,
  Users,
  Briefcase,
  UserCheck,
  Info,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PamiOrderedItem {
  id: string;
  prefixLabel: string;
  label: string;
}

interface PamiReponse {
  name: string;
  label: string;
  orderedItems: PamiOrderedItem[];
}

interface PamiLLMResultat {
  jobId: string;
  app: string;
  timestamp: number;
  data: unknown;
}

interface LLMSynthesis {
  synthese_personnality?: string;
  synthese_motivation?: string;
  synthese?: string;
  ideal_workplace?: string;
  ideal_manager?: string;
}

interface QuestionsFinales {
  langueMaternelle: boolean;
  bonnesConditions: boolean;
  reponsesSinceres: boolean;
}

interface PamiDonnees {
  type: "form";
  template: "pami";
  reponses: Record<string, PamiReponse>;
  questionsFinales?: QuestionsFinales;
  resultats?: PamiLLMResultat[];
}

interface CandidatInfo {
  prenom: string;
  nom: string;
  email: string;
  dateDebut?: number;
  dateFin?: number;
  tempsPasse?: number;
}

interface PamiResultsProps {
  donnees: unknown;
  candidatInfo: CandidatInfo;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TRAIT_PREFIXES = [
  "Statut",
  "Manag",
  "Equi",
  "Secu",
  "Auto",
  "Rea",
  "Var",
  "Rev",
  "H1",
  "H2",
  "N1",
  "N2",
  "E1",
  "E2",
  "A1",
  "A2",
  "C1",
  "C2",
  "O1",
  "O2",
];

const HEXACO_FACTORS = [
  {
    key: "H",
    label: "Honnêteté-Humilité",
    color: "#10b981",
    description:
      "Tendance à se montrer sincère et authentique, en évitant de manipuler ou d'exploiter les autres pour son propre intérêt.",
    facets: [
      {
        prefix: "H1",
        label: "Sincérité",
        description:
          "Tendance à être authentique et à éviter la tromperie ou la manipulation dans ses interactions avec les autres.",
        interpretations: {
          bas: "Flatte les autres ou prétend les apprécier pour obtenir leurs faveurs.",
          haut: "Honnête et franc(he), évite de tromper ou de manipuler les autres pour obtenir un avantage.",
        },
      },
      {
        prefix: "H2",
        label: "Intégrité",
        description:
          "Tendance à éviter la fraude, la corruption, et les comportements malhonnêtes pour obtenir des avantages personnels.",
        interpretations: {
          bas: "Veut gagner par des moyens injustes ou déloyaux, ou en profitant des autres.",
          haut: "Agit de manière désintéressée et transparente, en veillant à ne pas tirer avantage des autres.",
        },
      },
    ],
  },
  {
    key: "E",
    label: "Émotivité",
    color: "#eab308",
    description:
      "Tendance à être calme, résilient(e), et capable de gérer le stress de manière efficace.",
    facets: [
      {
        prefix: "N1",
        label: "Calme",
        description:
          "Tendance à être détendu(e) et serein(e), même face à des situations stressantes.",
        interpretations: {
          bas: "Éprouve fréquemment de l'anxiété et de l'inquiétude, se sentant facilement submergé(e) par les situations stressantes.",
          haut: "Démontre une grande sérénité et une capacité à rester détendu(e) même face au stress et aux challenges.",
        },
      },
      {
        prefix: "N2",
        label: "Gestion du stress",
        description:
          "Tendance à maintenir son calme et à rester efficace sous pression.",
        interpretations: {
          bas: "A du mal à maintenir son efficacité lorsque la pression monte. Tend à être envahi(e) par ses émotions dans les situations difficiles.",
          haut: "Reste efficace et orienté(e) vers les solutions même sous forte pression. Sait gérer ses émotions sans les laisser interférer avec son jugement.",
        },
      },
    ],
  },
  {
    key: "X",
    label: "eXtraversion",
    color: "#8b5cf6",
    description:
      "Tendance à rechercher activement la compagnie des autres, à se montrer énergique et à prendre plaisir dans les interactions sociales.",
    facets: [
      {
        prefix: "E1",
        label: "Affirmation de soi",
        description:
          "Tendance à être confiant(e), direct(e), et capable de prendre l'initiative dans les situations sociales ou professionnelles.",
        interpretations: {
          bas: "Tend à être réservé(e) et à éviter de prendre les devants ou d'exprimer ses opinions de manière directe.",
          haut: "Exprime ses opinions avec confiance, prend l'initiative et assume volontiers des rôles de leadership.",
        },
      },
      {
        prefix: "E2",
        label: "Sociabilité",
        description:
          "Tendance à apprécier la compagnie d'autrui et les interactions sociales.",
        interpretations: {
          bas: "Préfère la solitude ou les interactions en petits groupes, et se sent souvent plus à l'aise dans des environnements calmes ou privés.",
          haut: "Aime être entouré(e) de personnes, recherche les interactions sociales et tire son énergie de la présence des autres.",
        },
      },
    ],
  },
  {
    key: "A",
    label: "Agréabilité",
    color: "#06b6d4",
    description:
      "Tendance à être conciliant(e) et indulgent(e), en cherchant à maintenir des relations harmonieuses et à éviter les conflits.",
    facets: [
      {
        prefix: "A1",
        label: "Gestion des conflits",
        description:
          "Tendance à aborder les désaccords et les tensions interpersonnelles de manière constructive.",
        interpretations: {
          bas: "A du mal à faire face aux désaccords et aux tensions. Tend soit à les éviter au détriment de ses propres besoins, soit à y réagir de façon disproportionnée.",
          haut: "Aborde les désaccords avec assurance et bienveillance, en cherchant des solutions qui respectent les besoins de chacun.",
        },
      },
      {
        prefix: "A2",
        label: "Altruisme",
        description:
          "Tendance à être disposé(e) à aider les autres et à se montrer bienveillant(e).",
        interpretations: {
          bas: "Centré(e) sur ses propres intérêts et moins soucieux(se) des autres.",
          haut: "Montre une grande préoccupation pour les autres, avec une volonté constante d'aider et de soutenir ceux qui en ont besoin.",
        },
      },
    ],
  },
  {
    key: "C",
    label: "Conscienciosité",
    color: "#ef4444",
    description:
      "Tendance à être organisé(e) et persévérant(e) dans la réalisation de ses tâches, en poursuivant ses objectifs avec rigueur et discipline.",
    facets: [
      {
        prefix: "C1",
        label: "Autodiscipline",
        description:
          "Tendance à être capable de se concentrer sur son travail et de persévérer jusqu'à ce qu'il soit terminé.",
        interpretations: {
          bas: "Éprouve des difficultés à rester concentré(e) sur son travail et à le mener à bien, a tendance à se laisser distraire et à procrastiner.",
          haut: "Démontre une forte capacité à se concentrer et à terminer son travail avec persévérance, même face aux distractions.",
        },
      },
      {
        prefix: "C2",
        label: "Professionnalisme",
        description:
          "Tendance à s'acquitter de ses responsabilités avec sérieux et rigueur, en respectant les consignes, les délais et les engagements pris.",
        interpretations: {
          bas: "Peine à s'imposer la discipline nécessaire pour honorer ses engagements. Peut négliger les consignes ou les délais.",
          haut: "S'acquitte de ses responsabilités avec constance et rigueur, quel que soit son niveau de motivation.",
        },
      },
    ],
  },
  {
    key: "O",
    label: "Ouverture",
    color: "#f97316",
    description:
      "Tendance à explorer de nouvelles idées, à apprécier la diversité des expériences, et à s'engager dans des activités intellectuelles ou créatives.",
    facets: [
      {
        prefix: "O1",
        label: "Intellect",
        description:
          "Tendance à avoir une forte curiosité intellectuelle et un désir d'explorer de nouvelles perspectives.",
        interpretations: {
          bas: "Montre peu d'intérêt pour les idées nouvelles ou les concepts abstraits, préfère les choses concrètes et pratiques.",
          haut: "Démontre une forte curiosité intellectuelle et un intérêt prononcé pour les idées nouvelles, les discussions profondes et les questions complexes.",
        },
      },
      {
        prefix: "O2",
        label: "Créativité",
        description:
          "Tendance à générer des idées nouvelles et originales, en laissant libre cours à son imagination.",
        interpretations: {
          bas: "Peu attiré(e) par l'exploration d'idées nouvelles et la création. Préfère s'appuyer sur des approches éprouvées.",
          haut: "Génère facilement des idées originales et éprouve un réel plaisir à imaginer et à créer.",
        },
      },
    ],
  },
];

const MOTIVATION_DIMS = [
  {
    prefix: "Auto",
    label: "Autonomie",
    description:
      "Besoin de disposer d'une liberté d'action dans l'organisation et la réalisation de son travail.",
    interpretations: {
      bas: "S'accommode d'un cadre structuré et d'un encadrement proche, sans ressentir le besoin de maîtriser ses méthodes ou son organisation.",
      haut: "Éprouve un fort besoin de liberté dans la conduite de son travail et peut se sentir freiné(e) dans des environnements très hiérarchisés.",
    },
  },
  {
    prefix: "Equi",
    label: "Équilibre vie pro/perso",
    description:
      "Besoin de préserver une frontière claire entre vie professionnelle et vie personnelle.",
    interpretations: {
      bas: "Accepte volontiers des horaires contraignants ou une forte disponibilité sans que cela affecte sa motivation.",
      haut: "Place la qualité de vie au cœur de ses priorités et a besoin d'une organisation du travail qui préserve du temps pour sa vie personnelle.",
    },
  },
  {
    prefix: "Manag",
    label: "Qualité du management",
    description:
      "Besoin d'évoluer sous un encadrement bienveillant, qui reconnaît les contributions individuelles et favorise un dialogue ouvert.",
    interpretations: {
      bas: "Travaille efficacement quelle que soit la qualité de la relation hiérarchique, sans attendre de reconnaissance ou d'accompagnement particulier.",
      haut: "A besoin de se sentir soutenu(e) et reconnu(e) par sa hiérarchie pour s'investir pleinement.",
    },
  },
  {
    prefix: "Rea",
    label: "Réalisation de soi",
    description:
      "Besoin d'accomplissement professionnel, traduit par le désir de valoriser ses compétences et d'atteindre des objectifs ambitieux.",
    interpretations: {
      bas: "Accomplit son travail sans chercher particulièrement à se dépasser ou à obtenir une validation de ses compétences.",
      haut: "Tire une grande satisfaction de la réussite de ses missions et a besoin de se sentir en progression.",
    },
  },
  {
    prefix: "Rev",
    label: "Revenus",
    description:
      "Besoin d'une rémunération perçue comme juste et évolutive, en adéquation avec ses compétences et ses ambitions.",
    interpretations: {
      bas: "La dimension salariale n'est pas un moteur central de motivation.",
      haut: "La rémunération est un critère déterminant de motivation. Attache de l'importance aux perspectives d'évolution salariale.",
    },
  },
  {
    prefix: "Secu",
    label: "Sécurité professionnelle",
    description:
      "Besoin de stabilité et de prévisibilité dans sa situation professionnelle.",
    interpretations: {
      bas: "Tolère bien l'incertitude et les environnements changeants, voire les recherche.",
      haut: "La stabilité de l'emploi est une condition importante de son bien-être. Privilégie les structures établies.",
    },
  },
  {
    prefix: "Statut",
    label: "Statut social",
    description:
      "Besoin que son activité professionnelle lui confère une reconnaissance sociale, une visibilité et un prestige.",
    interpretations: {
      bas: "Le prestige de l'employeur ou la reconnaissance sociale associée à son poste n'influencent pas sa motivation professionnelle.",
      haut: "Être associé(e) à une organisation reconnue et occuper un poste valorisé socialement constituent des sources de motivation significatives.",
    },
  },
  {
    prefix: "Var",
    label: "Variété des tâches",
    description:
      "Besoin de diversité dans les activités et les projets professionnels, afin d'éviter la routine.",
    interpretations: {
      bas: "S'épanouit dans un périmètre de tâches stable et maîtrisé. La routine ne génère pas d'ennui.",
      haut: "A besoin de diversité et de renouvellement dans ses missions pour rester motivé(e).",
    },
  },
];

// ─── Utilities ────────────────────────────────────────────────────────────────

function computeScores(donnees: PamiDonnees): Record<string, number> {
  if (!donnees?.reponses || Object.keys(donnees.reponses).length === 0) {
    return {};
  }

  const rawScores: Record<string, number> = {};
  const maxScores: Record<string, number> = {};

  Object.values(donnees.reponses).forEach((reponse) => {
    reponse.orderedItems.forEach((item, position) => {
      const prefix = TRAIT_PREFIXES.find((p) => item.id.startsWith(p));
      if (!prefix) return;
      rawScores[prefix] = (rawScores[prefix] || 0) + (2 - position);
      maxScores[prefix] = (maxScores[prefix] || 0) + 2;
    });
  });

  const scores: Record<string, number> = {};
  TRAIT_PREFIXES.forEach((prefix) => {
    const max = maxScores[prefix] || 1;
    scores[prefix] = Math.round(((rawScores[prefix] || 0) / max) * 10);
  });

  return scores;
}

function getScoreColor(score: number): string {
  if (score <= 3) return "#ef4444";
  if (score <= 6) return "#f59e0b";
  return "#22c55e";
}

function getScoreLabel(score: number): string {
  if (score <= 1) return "Très bas";
  if (score <= 3) return "Bas";
  if (score <= 6) return "Dans la moyenne";
  if (score === 7) return "Au-dessus de la moyenne";
  if (score <= 8) return "Élevé";
  return "Très élevé";
}

// ─── ScoreBar ─────────────────────────────────────────────────────────────────

function ScoreBar({ score }: { score: number }) {
  const color = getScoreColor(score);
  const pct = (score / 10) * 100;

  return (
    <div className="mt-1 pt-2">
      <div className="flex items-center mb-6">
        <span className="text-xs font-semibold" style={{ color }}>
          {getScoreLabel(score)}
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-muted overflow-visible">
        <div
          className="absolute top-0 left-0 h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: color, opacity: 0.6 }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 rounded-full border-2 border-background"
          style={{
            left: `calc(${pct}% - 7px)`,
            width: 14,
            height: 14,
            backgroundColor: color,
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            zIndex: 2,
          }}
        />
        <div
          className="absolute font-bold text-xs"
          style={{ top: -20, left: `calc(${pct}% - 6px)`, color }}
        >
          {score}
        </div>
      </div>
      <div
        className="flex justify-between mt-2 text-muted-foreground"
        style={{ fontSize: 10 }}
      >
        <span>0</span>
        <span>5</span>
        <span>10</span>
      </div>
    </div>
  );
}

// ─── FacetCard ────────────────────────────────────────────────────────────────

function FacetCard({
  label,
  description,
  score,
  interpretations,
}: {
  label: string;
  description: string;
  score: number;
  interpretations: { bas: string; haut: string };
}) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <p className="font-semibold text-sm">{label}</p>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
        {description}
      </p>
      <div className="mt-3 space-y-1.5">
        <div className="flex items-start gap-2 text-xs">
          <span className="shrink-0 font-semibold text-red-500">Score bas :</span>
          <span className="text-muted-foreground leading-relaxed">{interpretations.bas}</span>
        </div>
        <div className="flex items-start gap-2 text-xs">
          <span className="shrink-0 font-semibold text-green-500">Score élevé :</span>
          <span className="text-muted-foreground leading-relaxed">{interpretations.haut}</span>
        </div>
      </div>
      <ScoreBar score={score} />
    </div>
  );
}

// ─── HexacoFactorCard ─────────────────────────────────────────────────────────

function HexacoFactorCard({
  factor,
  scores,
}: {
  factor: (typeof HEXACO_FACTORS)[number];
  scores: Record<string, number>;
}) {
  const [open, setOpen] = useState(false);
  const avgScore = Math.round(
    factor.facets.reduce((a, f) => a + (scores[f.prefix] ?? 5), 0) /
      factor.facets.length,
  );

  return (
    <Card>
      <button
        className="w-full text-left px-6 pt-5 pb-5 flex items-center gap-4 cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <div
          className="flex items-center justify-center shrink-0 rounded-full font-semibold text-base"
          style={{
            width: 40,
            height: 40,
            backgroundColor: `${factor.color}1a`,
            color: factor.color,
          }}
        >
          {factor.key}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="font-semibold text-sm">{factor.label}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            {factor.description}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Moyenne
            </p>
            <p
              className="text-2xl font-bold leading-none"
              style={{ color: factor.color }}
            >
              {avgScore}
            </p>
          </div>
          {open ? (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
      </button>
      {open && (
        <CardContent className="pt-0 pb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {factor.facets.map((facet) => (
              <FacetCard
                key={facet.prefix}
                label={facet.label}
                description={facet.description}
                score={scores[facet.prefix] ?? 5}
                interpretations={facet.interpretations}
              />
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ─── MotivationDimCard ────────────────────────────────────────────────────────

function MotivationDimCard({
  dim,
  score,
}: {
  dim: (typeof MOTIVATION_DIMS)[number];
  score: number;
}) {
  return (
    <Card>
      <div className="px-6 pt-5 pb-2 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{dim.label}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            {dim.description}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Score
          </p>
          <p
            className="text-2xl font-bold leading-none"
            style={{ color: getScoreColor(score) }}
          >
            {score}
          </p>
        </div>
      </div>
      <CardContent className="pt-0 pb-6">
        <div className="mt-1 space-y-1.5">
          <div className="flex items-start gap-2 text-xs">
            <span className="shrink-0 font-semibold text-red-500">Score bas :</span>
            <span className="text-muted-foreground leading-relaxed">{dim.interpretations.bas}</span>
          </div>
          <div className="flex items-start gap-2 text-xs">
            <span className="shrink-0 font-semibold text-green-500">Score élevé :</span>
            <span className="text-muted-foreground leading-relaxed">{dim.interpretations.haut}</span>
          </div>
        </div>
        <ScoreBar score={score} />
      </CardContent>
    </Card>
  );
}

// ─── InsightCard ──────────────────────────────────────────────────────────────

function InsightCard({
  icon,
  title,
  content,
}: {
  icon: React.ReactNode;
  title: string;
  content: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
          {content}
        </p>
      </CardContent>
    </Card>
  );
}

// ─── SyntheseSection ──────────────────────────────────────────────────────────

function parseSynthese(text: string): { title: string; bullets: string[] }[] {
  const parts = text.split(/\*\*([^*]+)\*\*/g);
  const result: { title: string; bullets: string[] }[] = [];
  for (let i = 1; i < parts.length; i += 2) {
    const title = (parts[i] ?? "").trim();
    const raw = parts[i + 1] ?? "";
    const bullets = raw
      .split("\n")
      .map((l) => l.replace(/^[-•*]\s*/, "").trim())
      .filter(Boolean);
    if (title) result.push({ title, bullets });
  }
  return result;
}

function getSyntheseIcon(title: string): React.ReactNode {
  const t = title.toLowerCase();
  if (
    t.includes("talent") ||
    t.includes("force") ||
    t.includes("atout") ||
    t.includes("motive") ||
    t.includes("motiv")
  )
    return <Star className="h-4 w-4 text-amber-500" />;
  if (
    t.includes("vigilan") ||
    t.includes("risque") ||
    t.includes("attention") ||
    t.includes("décourag") ||
    t.includes("decourag") ||
    t.includes("frein")
  )
    return <AlertTriangle className="h-4 w-4 text-orange-500" />;
  if (t.includes("travaill") || t.includes("manager") || t.includes("comment"))
    return <Users className="h-4 w-4 text-blue-500" />;
  return <Star className="h-4 w-4 text-muted-foreground" />;
}

function SyntheseCard({
  title,
  bullets,
}: {
  title: string;
  bullets: string[];
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? bullets : bullets.slice(0, 3);
  const hasMore = bullets.length > 3;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          {getSyntheseIcon(title)}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1.5">
          {visible.map((bullet, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-sm text-muted-foreground"
            >
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground/50 shrink-0" />
              <span className="leading-relaxed">{bullet}</span>
            </li>
          ))}
        </ul>
        {hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3 w-3" /> Voir moins
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" /> Voir plus (
                {bullets.length - 3} de plus)
              </>
            )}
          </button>
        )}
      </CardContent>
    </Card>
  );
}

// ─── QuestionsFinalesInfo ─────────────────────────────────────────────────────

function QuestionsFinalesInfo({ qf }: { qf: QuestionsFinales }) {
  const items = [
    { label: "Passé dans la langue maternelle", value: qf.langueMaternelle },
    { label: "Passé dans de bonnes conditions", value: qf.bonnesConditions },
    { label: "Répondu sincèrement et honnêtement", value: qf.reponsesSinceres },
  ];

  return (
    <div className="relative group inline-flex items-center">
      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 rounded-md border bg-popover p-3 shadow-md text-xs invisible group-hover:visible z-50 pointer-events-none">
        <p className="font-semibold mb-2 text-foreground">
          Conditions de passation
        </p>
        <ul className="space-y-1.5">
          {items.map(({ label, value }) => (
            <li key={label} className="flex items-center gap-2">
              <span
                className={
                  value ? "text-green-500 font-bold" : "text-red-500 font-bold"
                }
              >
                {value ? "✓" : "✗"}
              </span>
              <span className="text-foreground">{label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── PamiResultsEcharts ───────────────────────────────────────────────────────

export function PamiResultsEcharts({
  donnees,
  candidatInfo,
}: PamiResultsProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState("synthese");

  const pamiDonnees = donnees as PamiDonnees;
  const scores = useMemo(() => computeScores(pamiDonnees), [donnees]);

  const llmData = useMemo<LLMSynthesis | null>(() => {
    const latest = pamiDonnees.resultats?.at(-1);
    if (!latest?.data) return null;
    try {
      const parsed =
        typeof latest.data === "string" ? JSON.parse(latest.data) : latest.data;
      return (
        (parsed as { llm_synthesis?: LLMSynthesis })?.llm_synthesis ?? null
      );
    } catch {
      return null;
    }
  }, [donnees]);

  const tabs = [
    { key: "synthese", label: "Synthèse" },
    { key: "personnalite", label: "Personnalité" },
    { key: "motivation", label: "Motivation" },
  ];

  useEffect(() => {
    if (!chartRef.current) return;

    const myChart = echarts.init(chartRef.current);

    const regionColors: Record<string, string> = {
      "Honnêteté-humilité (H)": "#10b981",
      "Émotivité (E)": "#eab308",
      "eXtraversion (X)": "#8b5cf6",
      "Agréabilité (A)": "#06b6d4",
      "Conscienciosité (C)": "#ef4444",
      "Ouverture (O)": "#f97316",
      Motivation: "#3b82f6",
    };

    const rawData = [
      {
        name: "Sincérité",
        value: scores["H1"] ?? 5,
        region: "Honnêteté-humilité (H)",
      },
      {
        name: "Intégrité",
        value: scores["H2"] ?? 5,
        region: "Honnêteté-humilité (H)",
      },
      { name: "Calme", value: scores["N1"] ?? 5, region: "Émotivité (E)" },
      {
        name: "Gestion du stress",
        value: scores["N2"] ?? 5,
        region: "Émotivité (E)",
      },
      {
        name: "Affirmation de soi",
        value: scores["E1"] ?? 5,
        region: "eXtraversion (X)",
      },
      {
        name: "Sociabilité",
        value: scores["E2"] ?? 5,
        region: "eXtraversion (X)",
      },
      {
        name: "Gestion des conflits",
        value: scores["A1"] ?? 5,
        region: "Agréabilité (A)",
      },
      {
        name: "Altruisme",
        value: scores["A2"] ?? 5,
        region: "Agréabilité (A)",
      },
      {
        name: "Autodiscipline",
        value: scores["C1"] ?? 5,
        region: "Conscienciosité (C)",
      },
      {
        name: "Professionnalisme",
        value: scores["C2"] ?? 5,
        region: "Conscienciosité (C)",
      },
      { name: "Intellect", value: scores["O1"] ?? 5, region: "Ouverture (O)" },
      { name: "Créativité", value: scores["O2"] ?? 5, region: "Ouverture (O)" },
      { name: "Autonomie", value: scores["Auto"] ?? 5, region: "Motivation" },
      {
        name: "Équilibre vie pro/perso",
        value: scores["Equi"] ?? 5,
        region: "Motivation",
      },
      {
        name: "Qualité management",
        value: scores["Manag"] ?? 5,
        region: "Motivation",
      },
      {
        name: "Réalisation de soi",
        value: scores["Rea"] ?? 5,
        region: "Motivation",
      },
      { name: "Revenus", value: scores["Rev"] ?? 5, region: "Motivation" },
      {
        name: "Sécurité pro",
        value: scores["Secu"] ?? 5,
        region: "Motivation",
      },
      {
        name: "Statut social",
        value: scores["Statut"] ?? 5,
        region: "Motivation",
      },
      {
        name: "Variété des tâches",
        value: scores["Var"] ?? 5,
        region: "Motivation",
      },
    ];

    const seriesData = rawData.map((item) => ({
      value: item.value,
      name: item.name,
      region: item.region,
      itemStyle: { color: regionColors[item.region] },
    }));

    const uniqueRegions = Object.keys(regionColors);
    const legendDummyData = uniqueRegions.map((region) => ({
      name: region,
      value: 0,
      itemStyle: { color: regionColors[region] },
    }));

    const getSafeKey = (str: string) => str.replace(/[^a-zA-Z0-9]/g, "");
    const richTextStyles: Record<string, unknown> = {};

    uniqueRegions.forEach((region) => {
      const safeKey = getSafeKey(region);
      richTextStyles[`${safeKey}Badge`] = {
        backgroundColor: regionColors[region],
        color: "#fff",
        borderRadius: 15,
        padding: [4, 8],
        fontWeight: "bold",
        fontSize: 12,
        fontFamily: "sans-serif",
        shadowColor: "rgba(0,0,0,0.2)",
        shadowBlur: 3,
        shadowOffsetY: 1,
      };
      richTextStyles[`${safeKey}Name`] = {
        color: regionColors[region],
        fontWeight: "bold",
        fontSize: 13,
        fontFamily: "sans-serif",
      };
    });

    const option = {
      title: {
        text: "Profil PAMI — vue d'ensemble",
        left: "center",
        top: 16,
        textStyle: { fontSize: 15, fontFamily: "sans-serif", fontWeight: 600 },
      },
      tooltip: {
        trigger: "item",
        formatter: (params: {
          data?: { region: string };
          value: number;
          name: string;
        }) => {
          if (!params.data || params.value === 0) return "";
          return `<b>${params.name}</b> : ${params.value}/10<br/>Domaine : ${params.data.region}`;
        },
      },
      legend: {
        bottom: 8,
        type: "scroll",
        data: uniqueRegions,
        textStyle: { fontSize: 12, fontFamily: "sans-serif" },
      },
      toolbox: {
        show: true,
        feature: {
          saveAsImage: { show: true },
        },
      },
      polar: { radius: ["10%", "60%"], center: ["50%", "50%"] },
      angleAxis: { show: false },
      radiusAxis: {
        min: 0,
        max: 10,
        splitNumber: 5,
        z: 10,
        splitLine: {
          show: true,
          lineStyle: { type: "dotted", color: "rgba(0,0,0,0.15)", width: 1 },
        },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
      },
      series: [
        { type: "scatter", coordinateSystem: "polar", data: [] },
        {
          name: "Traits de personnalité",
          type: "pie",
          radius: ["10%", "60%"],
          center: ["50%", "50%"],
          roseType: "area",
          itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 1.5 },
          label: {
            show: true,
            formatter: (params: {
              data: { region: string };
              name: string;
              value: number;
            }) => {
              const safeKey = getSafeKey(params.data.region);
              return `{${safeKey}Name|${params.name}}  {${safeKey}Badge|${params.value}}`;
            },
            rich: richTextStyles,
          },
          labelLine: { show: true, length: 15, length2: 20, smooth: true },
          data: seriesData,
        },
        {
          name: "Legende",
          type: "pie",
          radius: [0, 0],
          itemStyle: { color: "transparent" },
          label: { show: false },
          labelLine: { show: false },
          data: legendDummyData,
        },
      ],
    };

    myChart.setOption(option);

    myChart.on("legendselectchanged", (params: any) => {
      const filtered = seriesData.filter(
        (item) => params.selected[item.region] !== false,
      );
      myChart.setOption({
        series: [{ name: "Traits de personnalité", data: filtered }],
      });
    });

    return () => {
      myChart.dispose();
    };
  }, [scores]);

  return (
    <div className="space-y-6">
      {/* ── Infos candidat ── */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>
                {candidatInfo.prenom} {candidatInfo.nom} — {candidatInfo.email}
              </span>
            </div>
            {candidatInfo.dateFin && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{formatDateTime(candidatInfo.dateFin)}</span>
              </div>
            )}
            {candidatInfo.tempsPasse != null && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{formatDuration(candidatInfo.tempsPasse)}</span>
              </div>
            )}
            {pamiDonnees.questionsFinales && (
              <QuestionsFinalesInfo qf={pamiDonnees.questionsFinales} />
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Bloc "Présentation du PAMI" ── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            Présentation du PAMI
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 text-sm text-muted-foreground space-y-3">
          <p>
            Développé par{" "}
            <strong className="text-foreground">Pipl Analytics</strong>, le{" "}
            <strong className="text-foreground">PAMI</strong> (Personality and
            Motivation Inventory) est un test psychométrique conçu pour explorer
            la personnalité et les motivations en contexte professionnel au
            travers de <strong className="text-foreground">40 questions</strong>
            , avec un temps de passation moyen de{" "}
            <strong className="text-foreground">10 minutes</strong>.
          </p>
          <div>
            <p className="mb-2">
              Le PAMI évalue{" "}
              <strong className="text-foreground">20 traits au total</strong> :
            </p>
            <ul className="space-y-1.5 ml-1">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground/50 shrink-0" />
                <span>
                  <strong className="text-foreground">
                    12 traits de personnalité
                  </strong>{" "}
                  se référant au modèle{" "}
                  <strong className="text-foreground">HEXACO</strong> (les Big 5
                  + le trait Honnêteté-Humilité)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground/50 shrink-0" />
                <span>
                  <strong className="text-foreground">
                    8 facteurs de motivation professionnelle
                  </strong>{" "}
                  se référant au{" "}
                  <strong className="text-foreground">
                    Work Values Inventory
                  </strong>{" "}
                  de Donald Super
                </span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* ── Onglets ── */}
      <div className="border-b -mx-4 md:-mx-8">
        <div className="flex flex-wrap px-4 md:px-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`shrink-0 px-5 py-3 text-sm transition-all cursor-pointer ${
                activeTab === tab.key
                  ? "font-semibold text-primary border-b-2 border-primary"
                  : "font-medium text-muted-foreground hover:text-foreground border-b-2 border-transparent"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Onglet Synthèse ── */}
      <div className={activeTab === "synthese" ? "space-y-4" : "hidden"}>
        <div>
          <h2 className="text-xl font-bold">Synthèse du profil</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Vue d'ensemble des 20 traits évalués par le PAMI.
          </p>
        </div>

        <Card>
          <CardContent className="p-0">
            <div
              ref={chartRef}
              style={{ width: "100%", height: "clamp(400px, 60vw, 720px)" }}
            />
          </CardContent>
        </Card>

        {llmData && (
          <div className="space-y-4">
            {llmData.synthese &&
              parseSynthese(llmData.synthese).map((section) => (
                <SyntheseCard
                  key={section.title}
                  title={section.title}
                  bullets={section.bullets}
                />
              ))}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {llmData.ideal_workplace && (
                <InsightCard
                  icon={<Briefcase className="h-4 w-4 text-blue-500" />}
                  title="Environnement de travail idéal"
                  content={llmData.ideal_workplace}
                />
              )}
              {llmData.ideal_manager && (
                <InsightCard
                  icon={<UserCheck className="h-4 w-4 text-violet-500" />}
                  title="Style de management idéal"
                  content={llmData.ideal_manager}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Onglet Personnalité ── */}
      <div className={activeTab === "personnalite" ? "space-y-4" : "hidden"}>
        <div>
          <h2 className="text-xl font-bold">Personnalité</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Analyse détaillée des 12 facettes de personnalité selon le modèle
            HEXACO.
          </p>
        </div>

        {llmData?.synthese_personnality && (
          <InsightCard
            icon={<Users className="h-4 w-4 text-blue-500" />}
            title="Synthèse personnalité"
            content={llmData.synthese_personnality}
          />
        )}

        <div className="space-y-4">
          {HEXACO_FACTORS.map((factor) => (
            <HexacoFactorCard
              key={factor.key}
              factor={factor}
              scores={scores}
            />
          ))}
        </div>
      </div>

      {/* ── Onglet Motivation ── */}
      <div className={activeTab === "motivation" ? "space-y-4" : "hidden"}>
        <div>
          <h2 className="text-xl font-bold">Motivation</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Analyse des 8 dimensions motivationnelles selon le Work Values
            Inventory de Donald Super.
          </p>
        </div>

        {llmData?.synthese_motivation && (
          <div className="space-y-4">
            {parseSynthese(llmData.synthese_motivation).map((section) => (
              <SyntheseCard
                key={section.title}
                title={section.title}
                bullets={section.bullets}
              />
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {MOTIVATION_DIMS.map((dim) => (
            <MotivationDimCard
              key={dim.prefix}
              dim={dim}
              score={scores[dim.prefix] ?? 5}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
